import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES } from '../../i18n';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const menuItems = [
    { text: <HomeIcon />, path: '/' },
    { text: t('nav.us'), path: '/nosotros' },
    { text: t('nav.tdl'), path: '/trabajadores-de-la-luz' },
    {
      text: t('nav.esenios'),
      path: '/esenios',
      submenu: [
        { text: t('nav.jeshua'), path: '/esenios-jeshua' },
        { text: t('nav.magdalena'), path: '/esenios-maria-magdalena' },
        { text: t('nav.madre'), path: '/esenios-maria-madre' },
        { text: t('nav.abuela'), path: '/esenios-abuela-ana' }
      ]
    },
    {
      text: t('nav.angeles'),
      path: '/angeles-y-arcangeles',
      submenu: [
        { text: t('nav.angeles_sub'), path: '/angeles-y-arcangeles/angeles' },
        { text: t('nav.miguel'), path: '/angeles-y-arcangeles/miguel' },
        { text: t('nav.metatron'), path: '/angeles-y-arcangeles/metatron' },
        { text: t('nav.gabriel'), path: '/angeles-y-arcangeles/gabriel' },
        { text: t('nav.rafael'), path: '/angeles-y-arcangeles/rafael' },
        { text: t('nav.chamuel'), path: '/angeles-y-arcangeles/chamuel' },
        { text: t('nav.jofiel'), path: '/angeles-y-arcangeles/jofiel' },
        { text: t('nav.uriel'), path: '/angeles-y-arcangeles/uriel' },
        { text: t('nav.zadquiel'), path: '/angeles-y-arcangeles/zadquiel' },
      ]
    },
    { text: t('nav.contacto'), path: '/contacto' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event, menuIndex) => {
    setMenuAnchor(event.currentTarget);
    setOpenSubmenu(menuIndex);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setOpenSubmenu(null);
  };

  const handleMobileSubmenuToggle = (menuIndex) => {
    setOpenSubmenu(openSubmenu === menuIndex ? null : menuIndex);
  };

  const handleLangChange = (lng) => {
    i18n.changeLanguage(lng);
    setLangAnchor(null);
  };

  const isActive = (path) => location.pathname === path;

  const isSubmenuActive = (submenuItems) => {
    return submenuItems.some(item => location.pathname === item.path);
  };

  const navBtnSx = (active) => ({
    color: active ? '#ff5722' : '#fff',
    fontWeight: active ? 'bold' : 'normal',
    px: 1,
    py: 1,
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
    textTransform: 'none',
    fontSize: { sm: '0.8rem', md: '0.9rem', lg: '1rem' },
    display: 'flex',
    alignItems: 'center'
  });

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: '#424a4a',
          boxShadow: 3
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ color: '#fff' }}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2
              }}
            >
              {menuItems.map((item, index) => (
                <React.Fragment key={item.path}>
                  {item.submenu ? (
                    <Button
                      aria-controls={`menu-${index}`}
                      aria-haspopup="true"
                      onClick={(e) => handleMenuOpen(e, index)}
                      sx={navBtnSx(isActive(item.path) || isSubmenuActive(item.submenu))}
                    >
                      {item.text}
                      {openSubmenu === index ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Button>
                  ) : (
                    <Button
                      component={Link}
                      to={item.path}
                      sx={navBtnSx(isActive(item.path))}
                    >
                      {item.text}
                    </Button>
                  )}
                </React.Fragment>
              ))}

              {/* Selector de idioma */}
              <Button
                onClick={(e) => setLangAnchor(e.currentTarget)}
                startIcon={<LanguageIcon />}
                endIcon={<ExpandMoreIcon fontSize="small" />}
                sx={navBtnSx(false)}
              >
                {i18n.language?.toUpperCase().slice(0, 2)}
              </Button>
              <Menu
                anchorEl={langAnchor}
                open={Boolean(langAnchor)}
                onClose={() => setLangAnchor(null)}
                PaperProps={{
                  sx: {
                    backgroundColor: '#424a4a',
                    color: '#fff',
                    minWidth: 180,
                    mt: 0.5,
                    maxHeight: 400,
                    overflowY: 'auto'
                  }
                }}
              >
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <MenuItem
                    key={lng}
                    onClick={() => handleLangChange(lng)}
                    sx={{
                      color: i18n.language === lng ? '#ff5722' : 'rgba(255,255,255,0.9)',
                      backgroundColor: i18n.language === lng ? 'rgba(255,87,34,0.2)' : 'transparent',
                      fontWeight: i18n.language === lng ? 700 : 400,
                      fontSize: '0.9rem',
                      '&:hover': { backgroundColor: 'rgba(255,87,34,0.15)' }
                    }}
                  >
                    <Box component="span" sx={{ mr: 1, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                      {lng.toUpperCase()}
                    </Box>
                    {LANGUAGE_NAMES[lng]}
                  </MenuItem>
                ))}
              </Menu>

              {/* Dropdown menu for submenus */}
              <Menu
                id={`menu-${openSubmenu}`}
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                MenuListProps={{ 'aria-labelledby': 'button' }}
                PaperProps={{
                  sx: {
                    backgroundColor: '#424a4a',
                    color: '#fff',
                    minWidth: '200px',
                    mt: 0.5
                  }
                }}
              >
                {openSubmenu !== null && menuItems[openSubmenu]?.submenu?.map((subItem) => (
                  <MenuItem
                    key={subItem.path}
                    component={Link}
                    to={subItem.path}
                    onClick={handleMenuClose}
                    sx={{
                      color: location.pathname === subItem.path ? '#ff5722' : '#fff',
                      fontWeight: location.pathname === subItem.path ? 'bold' : 'normal',
                      backgroundColor: location.pathname === subItem.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      fontSize: '0.9rem'
                    }}
                  >
                    {subItem.text}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#424a4a',
            color: '#fff'
          }
        }}
      >
        <List>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.path}>
              {item.submenu ? (
                <>
                  <ListItem
                    button
                    onClick={() => handleMobileSubmenuToggle(index)}
                    sx={{
                      backgroundColor: isActive(item.path) || isSubmenuActive(item.submenu) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                    }}
                  >
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.9rem',
                          color: isActive(item.path) || isSubmenuActive(item.submenu) ? '#ff5722' : '#fff',
                          fontWeight: isActive(item.path) || isSubmenuActive(item.submenu) ? 'bold' : 'normal',
                        }
                      }}
                    />
                    {openSubmenu === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItem>
                  <Collapse in={openSubmenu === index} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.submenu.map((subItem) => (
                        <ListItem
                          button
                          key={subItem.path}
                          component={Link}
                          to={subItem.path}
                          onClick={handleDrawerToggle}
                          sx={{
                            pl: 4,
                            backgroundColor: location.pathname === subItem.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                          }}
                        >
                          <KeyboardArrowRightIcon
                            fontSize="small"
                            sx={{
                              mr: 1,
                              color: location.pathname === subItem.path ? '#ff5722' : '#fff',
                            }}
                          />
                          <ListItemText
                            primary={subItem.text}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: '0.85rem',
                                color: location.pathname === subItem.path ? '#ff5722' : '#fff',
                                fontWeight: location.pathname === subItem.path ? 'bold' : 'normal',
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem
                  button
                  component={Link}
                  to={item.path}
                  onClick={handleDrawerToggle}
                  sx={{
                    backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9rem',
                        color: isActive(item.path) ? '#ff5722' : '#fff',
                        fontWeight: isActive(item.path) ? 'bold' : 'normal',
                      }
                    }}
                  />
                </ListItem>
              )}
            </React.Fragment>
          ))}

          {/* Selector de idioma en mobile */}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mt: 1 }} />
          <ListItem
            button
            onClick={(e) => setLangAnchor(e.currentTarget)}
            sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
          >
            <LanguageIcon sx={{ mr: 1.5, color: '#fff', fontSize: '1.2rem' }} />
            <ListItemText
              primary={`${i18n.language?.toUpperCase().slice(0, 2)} — ${LANGUAGE_NAMES[i18n.language] || ''}`}
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '0.9rem',
                  color: '#fff'
                }
              }}
            />
            <ExpandMoreIcon sx={{ color: '#fff' }} />
          </ListItem>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
            PaperProps={{
              sx: {
                backgroundColor: '#424a4a',
                color: '#fff',
                minWidth: 200,
                maxHeight: 350,
                overflowY: 'auto'
              }
            }}
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <MenuItem
                key={lng}
                onClick={() => handleLangChange(lng)}
                sx={{
                  color: i18n.language === lng ? '#ff5722' : 'rgba(255,255,255,0.9)',
                  backgroundColor: i18n.language === lng ? 'rgba(255,87,34,0.2)' : 'transparent',
                  fontWeight: i18n.language === lng ? 700 : 400,
                  fontSize: '0.9rem',
                  '&:hover': { backgroundColor: 'rgba(255,87,34,0.15)' }
                }}
              >
                <Box component="span" sx={{ mr: 1, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                  {lng.toUpperCase()}
                </Box>
                {LANGUAGE_NAMES[lng]}
              </MenuItem>
            ))}
          </Menu>
        </List>
      </Drawer>
    </>
  );
};

export default Header;
